import type { MenuItem } from '@/consts/navigation';
import type { TranslationKey } from '@/i18n/i18n';

import { getIcon } from './icons';
import type { MenuConfig } from './types';
import { normalizeLegacyPath, resolveLabel } from './utils';

/** Build URL for a legacy menu item from LegacyUrl, LegacyMode, LegacyQs. */
const buildLegacyUrl = (legacyUrl: string, legacyMode: string, legacyQs: string): string | undefined => {
  if (!legacyUrl) return undefined;
  const path = normalizeLegacyPath(legacyUrl);

  const params = new URLSearchParams();
  if (legacyMode) params.set('mode', legacyMode);
  if (legacyQs) {
    new URLSearchParams(legacyQs).forEach((value, key) => {
      params.set(key, value);
    });
  }

  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

/** Resolve the `to` URL based on item Type. */
const resolveUrl = (item: MenuConfig): string | undefined => {
  if (!('Type' in item)) return undefined;
  if (item.Type === 'native') return item.Route;
  return buildLegacyUrl(item.LegacyUrl, item.LegacyMode, item.LegacyQs);
};

/** Transform a unified MenuConfig item into a rendered MenuItem. */
export const transformMenuItem = (item: MenuConfig): MenuItem => {
  const menuItem: MenuItem = {
    text: resolveLabel(item.Label) as TranslationKey,
    to: resolveUrl(item),
    icon: item.Icon ? getIcon(item.Icon) : undefined,
    code: item.Code as MenuItem['code'],
    hidden: item.Hidden ?? false,
    isGroup: 'IsGroup' in item ? item.IsGroup : undefined
  };

  const childItems = item.Children;
  if (childItems && childItems.length > 0) {
    menuItem.children = childItems.map(transformMenuItem);
  }

  return menuItem;
};

/** Transform an array of unified items to rendered MenuItems. */
export const transformMenuItems = (items: MenuConfig[]): MenuItem[] => {
  return items.map(transformMenuItem);
};

/** Filter menu items by user roles. Empty Roles = visible to all. */
export const filterMenuByRoles = (items: MenuConfig[], userRoles: string[]): MenuConfig[] => {
  return items
    .filter((item) => {
      if (item.Roles.length === 0) return true;
      return item.Roles.some((role) => userRoles.includes(role));
    })
    .map((item) => {
      if (!item.Children) return item;
      const filteredChildren = filterMenuByRoles(item.Children, userRoles);
      if (filteredChildren.length === 0 && !('Type' in item)) return null;
      return { ...item, Children: filteredChildren } as MenuConfig;
    })
    .filter((item): item is MenuConfig => item !== null);
};
